import { useState } from "react";
import { Link } from "react-router-dom";
import { NAV_LINKS, SOCIALS, WHATSAPP } from "@/lib/site";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/inquiries", { email, type: "partner", message: "Partner / newsletter signup" });
      toast.success("Thank you — we'll be in touch.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#2C2A29] text-[#FFFFFF]" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="font-display text-4xl font-semibold">Afurnix</span>
            <p className="mt-5 text-[#C9C2B6] font-light max-w-sm leading-relaxed">
              A Foshan-based sourcing partner for premium furniture, custom cabinetry and whole-home interiors —
              serving discerning clients across the world.
            </p>
            <form onSubmit={submit} className="mt-8 flex max-w-sm border border-[#5E5A54]" data-testid="footer-partner-form">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Become a partner — your email"
                className="rounded-none bg-transparent border-0 text-[#FFFFFF] placeholder:text-[#8A8278] focus-visible:ring-0"
                data-testid="footer-email-input"
              />
              <Button type="submit" disabled={loading} className="rounded-none bg-[#9E8773] hover:bg-[#8A7969] px-4" data-testid="footer-submit-button">
                <ArrowRight size={18} />
              </Button>
            </form>
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <h4 className="label-eyebrow text-[#9E8773] mb-5">Explore</h4>
            <ul className="space-y-3">
              {NAV_LINKS.concat([{ label: "Contact", to: "/contact" }]).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-[#C9C2B6] hover:text-[#FFFFFF] transition-colors font-light" data-testid={`footer-link-${l.label.toLowerCase()}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="label-eyebrow text-[#9E8773] mb-5">Connect</h4>
            <ul className="space-y-3">
              {SOCIALS.map((s) => (
                <li key={s.label}>
                  <a href={s.href} target="_blank" rel="noopener noreferrer" className="text-[#C9C2B6] hover:text-[#FFFFFF] transition-colors font-light" data-testid={`social-${s.label.toLowerCase()}`}>
                    {s.label}
                  </a>
                </li>
              ))}
              <li>
                <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="text-[#9E8773] hover:text-[#FFFFFF] transition-colors font-light">
                  Chat on WhatsApp →
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#5E5A54]/40 flex flex-col sm:flex-row justify-between gap-4 text-[#8A8278] text-sm font-light">
          <span>© {new Date().getFullYear()} Afurnix. All rights reserved.</span>
          <div className="flex gap-6">
            <Link to="/admin/login" className="hover:text-[#FFFFFF] transition-colors" data-testid="footer-admin-link">Admin</Link>
            <span>Foshan · China</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
